import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';

// =====================
// TYPE DEFINITIONS
// =====================

export type SortDirection = 'asc' | 'desc';
export type TableVariant = 'default' | 'compact' | 'striped' | 'report';

/**
 * The variant (and sticky behavior) set on StandardTable flows to
 * StandardTableHeader through context, so callers set it in ONE place. The old
 * API required passing variant to the table AND the header separately — miss
 * the header and nothing visibly changed, which is why variant="report" had
 * zero adopters while report tables shipped purple.
 */
const TableSettingsContext = React.createContext<{
  variant: TableVariant;
  stickyHeader: boolean;
}>({ variant: 'default', stickyHeader: false });

interface StandardTableProps {
  children: React.ReactNode;
  className?: string;
  /**
   * 'default' = purple header (data tables). 'report' = grey header (rendered
   * report output). The header picks this up automatically.
   */
  variant?: TableVariant;
  loading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  /**
   * Let columns take their natural width (min-w-max) instead of squeezing into
   * the container. The container itself always scrolls horizontally when the
   * table is wider than it — content is never clipped.
   */
  horizontalScroll?: boolean;
  /** Keep the header row visible while the body scrolls. */
  stickyHeader?: boolean;
  /**
   * Cap the table's height (e.g. '60vh'); the body scrolls inside. Usually
   * paired with stickyHeader.
   */
  maxHeight?: string;
}

interface StandardTableHeaderProps {
  children: React.ReactNode;
  className?: string;
  variant?: TableVariant;
}

interface StandardTableHeaderCellProps {
  children: React.ReactNode;
  className?: string;
  isCheckbox?: boolean;
  sortable?: boolean;
  sortKey?: string;
  currentSortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (sortKey: string) => void;
  align?: 'left' | 'center' | 'right';
  width?: string;
  columnType?: 'actions' | 'fit' | 'expand' | 'auto' | 'narrow' | 'nowrap'; // Predefined column sizing behaviors
  responsiveHide?: 'sm' | 'md' | 'lg' | 'xl'; // Hide column below this breakpoint
}

interface StandardTableCellProps {
  children: React.ReactNode;
  className?: string;
  isCheckbox?: boolean;
  align?: 'left' | 'center' | 'right';
  colSpan?: number;
  responsiveHide?: 'sm' | 'md' | 'lg' | 'xl'; // Hide column below this breakpoint
}

interface StandardTableRowProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  onClick?: () => void;
  selected?: boolean;
  hoverable?: boolean;
}

interface StandardTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

// =====================
// MAIN TABLE CONTAINER
// =====================

/**
 * StandardTable - Main table container with responsive scrolling and loading states
 * 
 * @example
 * <StandardTable loading={isLoading} emptyMessage="No data found">
 *   <StandardTableHeader>...</StandardTableHeader>
 *   <StandardTableBody>...</StandardTableBody>
 * </StandardTable>
 */
export const StandardTable: React.FC<StandardTableProps> = ({
  children,
  className = "",
  variant = "default",
  loading = false,
  emptyMessage: _emptyMessage, // Reserved for future use
  emptyIcon: _emptyIcon, // Reserved for future use
  horizontalScroll = false,
  stickyHeader = false,
  maxHeight,
}) => {
  // Chevron paging for wide tables (horizontalScroll): overlay buttons on the
  // header bar edges, shown only on the side(s) with more columns off-screen.
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState<{ left: boolean; right: boolean }>({ left: false, right: false });

  const updateScrollState = React.useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);

  React.useEffect(() => {
    if (!horizontalScroll) return;
    updateScrollState();
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [horizontalScroll, updateScrollState, children]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollContainerRef.current;
    el?.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const variantClasses = {
    default: '',
    compact: 'compact-table',
    striped: 'striped-table',
    report: ''
  };

  const settings = React.useMemo(
    () => ({ variant, stickyHeader }),
    [variant, stickyHeader]
  );

  return (
    <div className={`standard-table relative ${variantClasses[variant]} ${className}`}>
      {horizontalScroll && canScroll.left && (
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          aria-label="Scroll table left"
          className="absolute left-0 top-0 z-20 flex h-12 items-center rounded-r-md bg-gradient-to-r from-black/25 to-transparent px-0.5 text-white hover:from-black/40"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {horizontalScroll && canScroll.right && (
        <button
          type="button"
          onClick={() => scrollByPage(1)}
          aria-label="Scroll table right"
          className="absolute right-0 top-0 z-20 flex h-12 items-center rounded-l-md bg-gradient-to-l from-black/25 to-transparent px-0.5 text-white hover:from-black/40"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      <div
        ref={scrollContainerRef}
        onScroll={horizontalScroll ? updateScrollState : undefined}
        className="standard-table-container"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        <table className={horizontalScroll ? 'min-w-max w-full' : undefined}>
          <TableSettingsContext.Provider value={settings}>
            {children}
          </TableSettingsContext.Provider>
        </table>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white dark:bg-background-dark-secondary bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
          <div className="flex items-center gap-2 text-primary-600">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm font-medium">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================
// TABLE HEADER
// =====================

/**
 * StandardTableHeader - Table header wrapper.
 *
 * Variant and sticky behavior come from the enclosing StandardTable via
 * context; the `variant` prop remains as an explicit override only.
 */
export const StandardTableHeader: React.FC<StandardTableHeaderProps> = ({
  children,
  className = "",
  variant
}) => {
  const settings = React.useContext(TableSettingsContext);
  const effective = variant ?? settings.variant;
  const headerClass = effective === 'report' ? 'report-table-header' : 'table-header';
  const stickyClass = settings.stickyHeader ? 'sticky' : '';

  return (
    // eslint-disable-next-line no-restricted-syntax -- this IS the standard table header
    <thead className={`${headerClass} ${stickyClass} ${className}`}>
      {children}
    </thead>
  );
};

// =====================
// TABLE HEADER CELL
// =====================

/**
 * StandardTableHeaderCell - Individual header cell with optional sorting
 * 
 * @example
 * // Sortable column
 * <StandardTableHeaderCell 
 *   sortable 
 *   sortKey="name"
 *   currentSortKey={sortField}
 *   sortDirection={sortDirection}
 *   onSort={handleSort}
 * >
 *   Name
 * </StandardTableHeaderCell>
 * 
 * // Checkbox column
 * <StandardTableHeaderCell isCheckbox>
 *   <input type="checkbox" onChange={handleSelectAll} />
 * </StandardTableHeaderCell>
 */
export const StandardTableHeaderCell: React.FC<StandardTableHeaderCellProps> = ({ 
  children, 
  className = "",
  isCheckbox = false,
  sortable = false,
  sortKey = "",
  currentSortKey = "",
  sortDirection = 'asc',
  onSort,
  align = 'left',
  width,
  columnType,
  responsiveHide
}) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  const baseClasses = isCheckbox 
    ? "table-checkbox-column" 
    : alignClasses[align];
  
  // Apply column type classes for intelligent sizing
  const columnTypeClasses = {
    actions: 'table-col-actions',
    fit: 'table-col-fit',
    expand: 'table-col-expand',
    auto: 'table-col-auto',
    narrow: 'table-col-narrow',
    nowrap: 'table-col-nowrap'
  };
  
  const widthClass = width ? width : '';
  const typeClass = columnType ? columnTypeClasses[columnType] : '';
  
  // Apply responsive hiding classes - use explicit class names for Tailwind JIT detection
  const responsiveHideClasses: Record<string, string> = {
    'sm': 'hidden sm:table-cell',
    'md': 'hidden md:table-cell',
    'lg': 'hidden lg:table-cell',
    'xl': 'hidden xl:table-cell'
  };
  const responsiveClass = responsiveHide ? responsiveHideClasses[responsiveHide] : '';

  // Render sortable header
  if (sortable && sortKey && onSort) {
    const isActive = currentSortKey === sortKey;
    
    const getSortIcon = () => {
      if (!isActive) {
        return <ChevronsUpDown className="w-4 h-4 text-white opacity-60" />;
      }
      return sortDirection === 'asc' ? 
        <ChevronUp className="w-4 h-4 text-white" /> : 
        <ChevronDown className="w-4 h-4 text-white" />;
    };

    return (
      <th className={`${baseClasses} ${widthClass} ${typeClass} ${responsiveClass} ${className}`}>
        <button 
          onClick={() => onSort(sortKey)}
          className="flex items-center gap-1 hover:opacity-90 transition-opacity w-full"
        >
          {children}
          {getSortIcon()}
        </button>
      </th>
    );
  }

  // Render non-sortable header
  return (
    <th className={`${baseClasses} ${widthClass} ${typeClass} ${responsiveClass} ${className}`}>
      {children}
    </th>
  );
};

// =====================
// TABLE BODY
// =====================

/**
 * StandardTableBody - Table body wrapper with empty state support
 */
export const StandardTableBody: React.FC<StandardTableBodyProps> = ({ 
  children,
  className = ""
}) => {
  return (
    <tbody className={`divide-y divide-border-light dark:divide-border-dark ${className}`}>
      {children}
    </tbody>
  );
};

// =====================
// TABLE ROW
// =====================

/**
 * StandardTableRow - Table row with optional hover and selection states
 * 
 * @example
 * <StandardTableRow 
 *   selected={isSelected}
 *   hoverable
 *   onClick={() => navigate(`/detail/${id}`)}
 * >
 *   <StandardTableCell>...</StandardTableCell>
 * </StandardTableRow>
 */
export const StandardTableRow: React.FC<StandardTableRowProps> = ({ 
  children,
  className = "",
  id,
  onClick,
  selected = false,
  hoverable = true
}) => {
  const hoverClass = hoverable && onClick ? 'cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary' : '';
  const selectedClass = selected ? 'bg-primary-50 dark:bg-primary-900 dark:bg-opacity-20' : '';

  return (
    <tr 
      id={id}
      className={`${hoverClass} ${selectedClass} ${className} transition-colors`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

// =====================
// TABLE CELL
// =====================

/**
 * StandardTableCell - Individual data cell
 * 
 * @example
 * <StandardTableCell align="right">$1,234.56</StandardTableCell>
 * <StandardTableCell isCheckbox>
 *   <input type="checkbox" />
 * </StandardTableCell>
 */
export const StandardTableCell: React.FC<StandardTableCellProps> = ({ 
  children, 
  className = "",
  isCheckbox = false,
  align = 'left',
  colSpan,
  responsiveHide
}) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  const baseClasses = isCheckbox 
    ? "table-checkbox-cell" 
    : alignClasses[align];
  
  // Apply responsive hiding classes - use explicit class names for Tailwind JIT detection
  const responsiveHideClasses: Record<string, string> = {
    'sm': 'hidden sm:table-cell',
    'md': 'hidden md:table-cell',
    'lg': 'hidden lg:table-cell',
    'xl': 'hidden xl:table-cell'
  };
  const responsiveClass = responsiveHide ? responsiveHideClasses[responsiveHide] : '';
  
  return (
    <td className={`${baseClasses} ${responsiveClass} ${className}`} colSpan={colSpan}>
      {children}
    </td>
  );
};

// =====================
// UTILITY COMPONENTS
// =====================

/**
 * EmptyTableState - Display when table has no data
 */
export const EmptyTableState: React.FC<{
  message?: string;
  icon?: React.ReactNode;
  colSpan: number;
}> = ({ message = "No data available", icon, colSpan }) => {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center">
        <div className="flex flex-col items-center gap-3">
          {icon && <div className="text-text-tertiary dark:text-text-dark-tertiary">{icon}</div>}
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {message}
          </p>
        </div>
      </td>
    </tr>
  );
};

/**
 * LoadingTableState - Display when table is loading
 */
export const LoadingTableState: React.FC<{
  colSpan: number;
  rows?: number;
}> = ({ colSpan, rows = 5 }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          <td colSpan={colSpan} className="px-4 py-4">
            <div className="animate-pulse flex space-x-4">
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded"></div>
              </div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};

// =====================
// EXPORTS
// =====================

export default StandardTable;
